import { useQuery } from '@tanstack/react-query';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { storySetupPdfQueryKeys } from './storySetupPdfQueryKeys';
import { ProgressTrackerSettingType, IStoryContent } from '@opengov/stories-shared/types';
import { FLAG_ADD_PROPOSALS_TO_STORIES } from '@opengov/stories-shared/constants';
import { fetchMergeTags } from '../utils/fetchMergeTags';
import { generateHTML, getFullPageContent } from '@opengov/stories-shared/utils/story';
import { sortAndOrderStoryContents } from '@opengov/stories-shared/utils/general';
import { replaceStoryContentsWithMergeTagValues } from '@opengov/stories-shared/utils/mergeTag';
import { getStoryDetailsForSetupPDF } from '../../../../graphQL/queries/publicationsQueries';
import { postJson } from '../../../../utils/fetchUtils';

export type IStoryHtmlContent = {
  html: string;
  storyCustomCss: string;
  publicationCustomCss: string;
  title: string;
};

export type UseFetchStoryHtmlContentOptions = {
  storyId: string;
  entityId: number;
  progressTrackerSettings?: ProgressTrackerSettingType[];
  enabled?: boolean;
};

type IStoryDetailsResponse = {
  _id: string;
  entityId: number;
  title: string;
  description: string;
  customCssCode: string;
  isFollowable: boolean;
  settings: {
    fontsHeadline: string;
    fontsParagraph: string;
  };
  contents: IStoryContent[];
  folder: {
    publication: {
      customCssCode: string;
    };
  };
};

type IStoryDetailsGraphQLResponse = {
  data?: {
    storyData?: IStoryDetailsResponse;
  };
  errors?: Array<{ message: string }>;
};

/**
 * Fetches story details via GraphQL
 */
const fetchStoryDetails = async (storyId: string): Promise<IStoryDetailsResponse> => {
  const response = await postJson<IStoryDetailsGraphQLResponse>('/api/data/objects/stories/v1/graphql', {
    query: getStoryDetailsForSetupPDF,
    variables: { id: storyId }
  });
  if (response.data?.storyData) {
    return response.data.storyData;
  }
  throw new Error('Failed to fetch story details');
};

/**
 * Generates HTML content for story preview
 */
const generateStoryHtmlContent = async (
  storyId: string,
  entityId: number,
  progressTrackerSettings: ProgressTrackerSettingType[] = []
): Promise<IStoryHtmlContent> => {
  // 1. Fetch story details
  const storyDetails = await fetchStoryDetails(storyId);
  const { title, description, customCssCode, contents, _id } = storyDetails;
  const publicationCustomCss = storyDetails.folder?.publication?.customCssCode || '';

  // 2. Sort and order story contents
  let sortedStoryContents = sortAndOrderStoryContents(contents);

  // 3. Check feature flag for proposals content
  const flags = await coreFeatureFlagEffects.getFlags();
  const canAddProposalsContent = Boolean(flags && flags[FLAG_ADD_PROPOSALS_TO_STORIES]);

  // 4. Fetch and apply merge tags (conditionally)
  if (canAddProposalsContent) {
    const mergeTags = await fetchMergeTags(storyId, entityId);
    sortedStoryContents = replaceStoryContentsWithMergeTagValues(sortedStoryContents, mergeTags);
  }

  // 5. Check for progress tracker
  const isStoryWithProgressTracker = sortedStoryContents.some(
    (content) => Array.isArray(content.contentChartConfig) && content.contentChartConfig.length > 0
  );

  // 6. Generate full page content
  const { content, containers } = getFullPageContent(
    sortedStoryContents,
    isStoryWithProgressTracker,
    storyDetails.entityId,
    progressTrackerSettings
  );

  // 7. Generate HTML
  const html = generateHTML({
    id: _id,
    title,
    description,
    fonts: [],
    content,
    hasGallery: true,
    isForCustomCssInspector: true,
    options: {},
    isFollowable: false,
    isStoryWithProgressTracker,
    containers,
    addIgnorePortalScript: true
  });

  return {
    html,
    storyCustomCss: customCssCode || '',
    publicationCustomCss,
    title
  };
};

/**
 * Hook to fetch story HTML content for PDF preview
 * Directly calls APIs without using Redux effects
 */
export function useFetchStoryHtmlContent({
  storyId,
  entityId,
  progressTrackerSettings = [],
  enabled = true
}: UseFetchStoryHtmlContentOptions) {
  return useQuery<IStoryHtmlContent>({
    queryKey: storySetupPdfQueryKeys.storyHtmlContent(storyId),
    queryFn: () => generateStoryHtmlContent(storyId, entityId, progressTrackerSettings),
    enabled: enabled && !!storyId && !!entityId,
    staleTime: 0,
    cacheTime: 0
  });
}
